import { Component, OnInit, OnDestroy } from "@angular/core";
import { Location } from "@angular/common";
import { Router, ActivatedRoute } from "@angular/router";
import { AreaMapRequest } from "@models/map";
import { ProgramEditorService } from "../../../common/program-editors/program-editor.service";
import { Subscription } from "rxjs/Subscription";
import { MessageService } from "primeng/components/common/messageservice";
import { RequestService } from "../../request.service";
import { environment } from "../../../../environments/environment";
import { TypesService } from "../../../common/types.service";

import {
    Program,
    ProgramContactInfo,
    ProgramDetails,
    ProgramFunding,
    ProgramServices,
    ProgramTimings,
    ProgramGradesServed,
    ProgramProgramsOffered,
    ProgramMealsServed,
    ProgramRequestSubmission
} from "@models/program";

declare var grecaptcha: any;

@Component({
    templateUrl: "./edit-request.component.html"
})

export class EditRequestComponent implements OnInit, OnDestroy {
    public id: number;
    public program: Program;
    public subscription: Subscription;
    public tabSubscription: Subscription;
    public contactInfoValidSub: Subscription;
    public submissionValidSub: Subscription;
    public currentTab: number;
    public initialized: boolean = false;
    public showCaptchaError: boolean = false;

    public contactInfoValid: boolean = false;
    public submissionValid: boolean = false;

    public captcha: any;

    constructor(private location: Location,
        private router: Router,
        private route: ActivatedRoute,
        private editorService: ProgramEditorService,
        private requestService: RequestService,
        private msgService: MessageService,
        private typeService: TypesService) { }

    ngOnInit(): void {
        this.editorService.dataReady = false;
        this.editorService.agencies = undefined;
        this.id = +this.route.snapshot.params["id"];
        this.getCities();
        this.getAgencies();
        this.getProgram();
        this.subscribeToProgram();
        this.subscribeToCurrentTab();
        this.subscribeToContactInfoValidity();
        this.subscribeToSubmissionValidity();
        this.changeTab(1);
    }

    getCities(): void {
        if (this.editorService.cities == undefined) {
            this.typeService.getCities().subscribe(
                response => {
                    this.editorService.cities = response;
                    this.editorService.completeCitiesFetch();
                }
            );
        }
    }

    getAgencies(): void {
        this.typeService.getAgencies().subscribe(
            response => {
                this.editorService.agencies = response;
                this.editorService.completeAgenciesFetch();
            }
        );
    }

    ngOnDestroy(): void {
        if (this.subscription != undefined) {
            this.subscription.unsubscribe();
        }
        if (this.tabSubscription != undefined) {
            this.tabSubscription.unsubscribe();
        }
        if (this.contactInfoValidSub != undefined) {
            this.contactInfoValidSub.unsubscribe();
        }
        if (this.submissionValidSub != undefined) {
            this.submissionValidSub.unsubscribe();
        }
    }

    getProgram(): void {
        this.requestService.getEditableProgram(this.id).subscribe(
            response => {
                let p = response;
                p.contactInfo.id = undefined;
                p.details.id = undefined;
                p.details.funding.id = undefined;
                p.services.timing.id = undefined;
                p.services.gradesServed.id = undefined;
                p.services.programsOffered.id = undefined;
                p.services.mealsServed.id = undefined;
                this.editorService.updateProgram(p);
            }
        );
    }

    subscribeToSubmissionValidity(): void {
        this.editorService.submissionValid$.subscribe(x => {
            this.submissionValid = x;
            this.showHideCaptchaError();
        });
    }

    subscribeToContactInfoValidity(): void {
        this.editorService.contactInfoValid$.subscribe(x => this.contactInfoValid = x);
    }

    subscribeToCurrentTab(): void {
        this.tabSubscription = this.editorService.currentTab$.subscribe(x => {
            this.currentTab = x;
            if (x === 4 && this.initialized) {
                this.renderCaptcha();
                this.showHideCaptchaError();
            }
            if (!this.initialized) {
                this.initialized = true;
            }
        });
    }

    subscribeToProgram(): void {
        this.subscription = this.editorService.program$.subscribe(x => this.program = x);
    }


    back(): void {
        if (!this.contactInfoValid && !environment.editorBypassValidation) {
            this.editorService.updateShowContactInfoErrors(true);
            this.changeTab(1);
            return;
        } else {
            this.editorService.updateShowContactInfoErrors(false);
        }

        if (this.currentTab === 1) {
            this.cancel();
        } else {
            let tab = this.currentTab - 1;
            this.changeTab(tab);
        }
    }

    next(): void {
        if (!this.contactInfoValid && !environment.editorBypassValidation) {
            this.editorService.updateShowContactInfoErrors(true);
            this.changeTab(1);
            return;
        } else {
            this.editorService.updateShowContactInfoErrors(false);
        }
        if (this.currentTab === 4) {
            console.log("submitting");
            this.submit();
        } else {
            let tab = this.currentTab + 1;
            this.changeTab(tab);
        }
    }

    moveToTab(tab: number): void {
        tab = +tab;
        if (environment.editorBypassValidation) {
            this.changeTab(tab);
            return;
        }

        if (!this.contactInfoValid) {
            this.editorService.updateShowContactInfoErrors(true);
            this.changeTab(1);
            return;
        } else {
            this.editorService.updateShowContactInfoErrors(false);
        }

        this.changeTab(tab);
    }

    changeTab(tab: number) {
        if (this.currentTab === tab) { return; }
        let link: string = "/request-edit/" + this.id + "/";
        switch (tab) {
            case 1:
                link += "contact-info";
                break;
            case 2:
                link += "details";
                break;
            case 3:
                link += "services";
                break;
            case 4:
                link += "submit";
                break;
        }

        this.router.navigate([link]);
    }

    cancel(): void {
        let link = "public-profile/" + this.id;
        this.router.navigate([link]);
    }

    renderCaptcha(i?: number): void {
        if (typeof (window.grecaptcha) === "undefined") {
            if (i == undefined) {
                i = 1;
            } else {
                i++;
            }
            if (i > 300) {
                setTimeout(() => this.renderCaptcha(i), 100);
            }
            return;
        }

        if (this.captcha == undefined) {

            let successCallback = (response) => {
                console.log(response);
                this.captchaSuccess(response);
            };

            let expiredCallback = () => {
                this.captchaExpired();
            };

            if (this.captcha == undefined) {
                console.log("creating captcha");
                this.captcha = window.grecaptcha.render("cap", {
                    "sitekey": "6LcJ7DUUAAAAALwM0aEWvcu0bcnFiDxVqZ1GBeAI",
                    "callback": successCallback,
                    "expired-callback": expiredCallback
                });

            }
        }
    }

    captchaSuccess(response): void {
        if (this.program.submit != undefined) {
            this.program.submit.captchaCode = response;
            this.editorService.updateSubmitValidation.next();
            this.showHideCaptchaError();
        }
    }

    captchaExpired(): void {
        if (this.program.submit != undefined) {
            this.program.submit.captchaCode = undefined;
            this.renderCaptcha();
        }
    }

    showHideCaptchaError(): void {
        let show = false;
        if (this.editorService.getShowSubmitErrors() && this.program.submit.captchaCode == undefined && this.currentTab === 4) {
            show = true;
        }

        this.showCaptchaError = show;
    }

    submit(): void {
        if (!this.contactInfoValid) {
            this.editorService.updateShowContactInfoErrors(true);
            this.changeTab(1);
        } else if (!this.submissionValid) {
            this.editorService.updateShowSubmitErrors(true);
            this.showHideCaptchaError();
            this.changeTab(4);
        } else {
            this.editorService.updateShowContactInfoErrors(false);
            this.editorService.updateShowSubmitErrors(false);

            this.program.id = this.id;

            this.requestService.submitEditRequest(this.program).subscribe(
                response => {
                    this.msgService.add({
                        severity: "success",
                        summary: "Success",
                        detail: response.message
                    });
                    this.router.navigate(["/map"]);
                },
                error => {
                    console.log(error);
                    this.msgService.add({
                        severity: "danger",
                        summary: "An Error Occurred",
                        detail: error.message
                    });
                }
            );
        }
    }
}



// WEBPACK FOOTER //
// C:/WORK/Projects/ActNow_NG2/src/app/requests/request/edit/edit-request.component.ts